export type PlanTier = "free" | "pro";

export type PlanLimits = {
  maxCollections: number | null;
  maxPlants: number | null;
  allowsCustomMaps: boolean;
  maxPhotosPerPlant: number | null;
};

export const PRO_PRICE_TEXT = "US$7.99";

/** ISO code for the price above. Stated in the UI so the amount is unambiguous. */
export const PRO_PRICE_CURRENCY = "USD";

/**
 * Length of the introductory free trial.
 *
 * Not configured anywhere yet — the RevenueCat Test Store cannot hold a trial,
 * and a real one is an introductory offer created in App Store Connect and Play
 * Console. Until then this is the length the copy promises, not one anybody
 * receives. See REVENUECAT_SETUP.md.
 */
export const PRO_TRIAL_DAYS = 14;

/**
 * Mirrors the `plan_limits` table. The database is the real gate; this copy
 * exists so an offline client can disable the button before the write fails.
 */
export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: {
    maxCollections: 1,
    maxPlants: 10,
    allowsCustomMaps: false,
    maxPhotosPerPlant: 0,
  },
  pro: {
    maxCollections: null,
    maxPlants: null,
    allowsCustomMaps: true,
    maxPhotosPerPlant: null,
  },
};

export type Allowance = {
  allowed: boolean;
  limit: number | null;
  used: number;
  remaining: number | null;
  message: string | null;
};

function allowance(
  used: number,
  limit: number | null,
  noun: string,
): Allowance {
  if (limit === null) {
    return { allowed: true, limit: null, used, remaining: null, message: null };
  }
  const remaining = Math.max(limit - used, 0);
  return {
    allowed: remaining > 0,
    limit,
    used,
    remaining,
    message:
      remaining > 0
        ? null
        : `Free accounts include ${limit} ${limit === 1 ? noun : `${noun}s`}. Upgrade to Pro for unlimited.`,
  };
}

export function collectionAllowance(tier: PlanTier, used: number): Allowance {
  return allowance(used, PLAN_LIMITS[tier].maxCollections, "collection");
}

export function plantAllowance(tier: PlanTier, used: number): Allowance {
  return allowance(used, PLAN_LIMITS[tier].maxPlants, "plant");
}

export function photoAllowance(tier: PlanTier, used: number): Allowance {
  const limit = PLAN_LIMITS[tier].maxPhotosPerPlant;
  // Free accounts cannot upload plant photos at all (Pro-only).
  if (limit === 0) {
    return {
      allowed: false,
      limit: 0,
      used,
      remaining: 0,
      message:
        "Plant photos are included with Florafolio Pro. Upgrade to upload photos.",
    };
  }
  return allowance(used, limit, "photo");
}

export function allowsCustomMaps(tier: PlanTier) {
  return PLAN_LIMITS[tier].allowsCustomMaps;
}

/**
 * Postgres raises plan-limit violations as P0001 with a human-readable
 * message, so surfacing it directly beats a generic failure toast.
 */
export function planLimitMessage(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const candidate = error as { code?: string; message?: string };
  if (candidate.code !== "P0001" || !candidate.message) return null;
  return candidate.message.includes("Florafolio Pro")
    ? candidate.message
    : null;
}
